import React, { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import OrderTicketModal from '../components/OrderTicketModal';
import SwingResultsTable from '../components/SwingResultsTable';
import SwingHelpModal from '../components/SwingHelpModal';
import StockChartModal from '../components/StockChartModal';
import { STOCK_INDICES, getAllIndices } from '../constants/stockIndices';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const SwingModelPage = () => {
  const [searchParams] = useSearchParams();
  const isMobileEmbed = searchParams.get('tv_embed') === '1';

  const [selectedIndex, setSelectedIndex] = useState('NIFTY 50');
  
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  
  const [selectedStockSymbol, setSelectedStockSymbol] = useState('');
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  
  // Chart modal states
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [selectedSymbolForChart, setSelectedSymbolForChart] = useState(null);
  
  // CSV Upload states
  const [stockSource, setStockSource] = useState('index'); // 'index' or 'csv'
  const [csvStocks, setCsvStocks] = useState([]);
  const [csvFileName, setCsvFileName] = useState('');
  const fileInputRef = useRef(null);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          if (!results.data || results.data.length === 0) {
            setError('CSV file is empty');
            return;
          }

          const firstRow = results.data[0];
          if (!firstRow.Symbol && !firstRow.symbol) {
            setError('CSV must have a "Symbol" column');
            return;
          }

          const symbols = results.data
            .map(row => row.Symbol || row.symbol)
            .filter(symbol => symbol && symbol.trim() !== '');

          if (symbols.length === 0) {
            setError('No valid symbols found in CSV');
            return;
          }

          const normalizedSymbols = symbols.map(symbol => {
            if (symbol.endsWith('.NSE') || symbol.endsWith('.NS')) {
              return symbol;
            }
            return symbol + '.NSE';
          });

          setCsvStocks(normalizedSymbols);
          setStockSource('csv');
          setCsvFileName(file.name);
          setError(null);
          
          console.log(`✅ Loaded ${normalizedSymbols.length} stocks from CSV:`, normalizedSymbols);
        } catch (err) {
          setError(`Failed to parse CSV: ${err.message}`);
        }
      },
      error: (err) => {
        setError(`CSV parsing error: ${err.message}`);
      }
    });

    event.target.value = '';
  };

  const handleDownloadResults = () => {
    if (!results || !results.all_results || results.all_results.length === 0) {
      alert('No results to download');
      return;
    }

    try {
      const worksheet = XLSX.utils.json_to_sheet(results.all_results);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Swing Analysis');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
      });
      
      const timestamp = new Date().toISOString().split('T')[0];
      saveAs(data, `TrendVector_Swing_Scan_${timestamp}.xlsx`);
      
      console.log('✅ Results downloaded successfully');
    } catch (err) {
      console.error('Download error:', err);
      setError(`Failed to download results: ${err.message}`);
    }
  };

  const handleRunScan = async () => {
    setLoading(true);
    setError(null);
    setResults(null);
    setProgress(0);
    setProgressMessage('Initializing analysis...');
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Please log in to run the analysis');
      }
      
      const tickersToAnalyze = stockSource === 'csv' 
        ? csvStocks 
        : STOCK_INDICES[selectedIndex];
      
      if (!tickersToAnalyze || tickersToAnalyze.length === 0) {
        throw new Error('No stocks selected for analysis');
      }
      
      const requestData = {
        tickers: tickersToAnalyze
      };
      
      console.log('🚀 Starting Swing analysis...');
      console.log('Request:', requestData);
      
      // Step 1: Start the analysis job
      const response = await fetch(`${API_BASE_URL}/api/v1/models/start-swing-scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to start analysis');
      }
      
      const { job_id } = await response.json();
      console.log('Analysis job started:', job_id);
      
      // Step 2: Connect to WebSocket for progress updates
      const wsProtocol = API_BASE_URL.startsWith('https') ? 'wss' : 'ws';
      const wsBaseUrl = API_BASE_URL.replace('https://', '').replace('http://', '');
      const wsUrl = `${wsProtocol}://${wsBaseUrl}/api/v1/models/ws/swing-status/${job_id}`;
      
      console.log('Connecting to WebSocket:', wsUrl);
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('✅ WebSocket connection established');
        setProgressMessage('Connected. Waiting for updates...');
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message received:', data);
          
          if (data.status === 'progress') {
            setProgress(data.progress || 0);
            setProgressMessage(data.message || 'Processing...');
          } else if (data.status === 'complete') {
            setProgress(1.0);
            setProgressMessage(data.message || 'Analysis complete!');
            setResults(data.results || {});
            setLoading(false);
            ws.close();
          } else if (data.status === 'error') {
            throw new Error(data.message || data.error || 'Analysis failed');
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
          setError(err.message);
          setLoading(false);
          ws.close();
        }
      };
      
      ws.onclose = () => {
        console.log('WebSocket connection closed');
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setError('Connection error. Please try again.');
        setLoading(false);
      };
      
    } catch (err) {
      setError(err.message);
      console.error('Analysis error:', err);
      setLoading(false);
    }
  };

  const handleTradeClick = (symbol) => {
    setSelectedStockSymbol(symbol);
    setIsOrderModalOpen(true);
  };

  const handleOrderSuccess = (orderData) => {
    console.log('✅ Order placed successfully:', orderData);
  };

  const handleModalClose = () => {
    setIsOrderModalOpen(false);
    setSelectedStockSymbol('');
  };

  // Get stock objects from all_results for display
  const getStockDetails = (symbolList) => {
    if (!results || !results.all_results || !symbolList) return [];
    return results.all_results.filter(stock => symbolList.includes(stock.symbol));
  };

  return (
    <div className="min-h-screen flex flex-col">
      {!isMobileEmbed && <Navbar />}

      <main className="flex-1 bg-gray-50">
        <div className={`max-w-7xl mx-auto px-4 lg:px-8 ${isMobileEmbed ? 'py-3' : 'py-8'}`}>
          {!isMobileEmbed && (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
              <div className="bg-gradient-to-r from-orange-600 to-amber-600 px-6 py-4">
                <h1 className="text-2xl font-bold text-white">Swing Trading Model</h1>
                <p className="text-orange-100 mt-1">Multi-timeframe analysis for short-term swing trading opportunities</p>
              </div>
            </div>
          )}

          {/* Filter Controls */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            {/* Header with How to Use button */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">📊 Analysis Parameters</h2>
              <button
                onClick={() => setIsHelpModalOpen(true)}
                className="flex items-center px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                How to use
              </button>
            </div>
            
            {/* Stock Source Selection */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex flex-wrap items-center gap-4">
                {/* Source Toggle Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setStockSource('index')}
                    className={`px-4 py-2 rounded-md font-medium transition-colors ${
                      stockSource === 'index'
                        ? 'bg-orange-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    📊 Select Index
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className={`px-4 py-2 rounded-md font-medium transition-colors ${
                      stockSource === 'csv'
                        ? 'bg-orange-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    📤 Upload CSV
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>

                {/* Index Selector */}
                {stockSource === 'index' && (
                  <div className="flex-1 min-w-[200px]">
                    <select
                      value={selectedIndex}
                      onChange={(e) => setSelectedIndex(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      {getAllIndices().map(index => (
                        <option key={index} value={index}>{index}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Stock Count Display */}
                <div className="text-sm font-medium text-gray-700">
                  {stockSource === 'index' ? (
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full">
                      {STOCK_INDICES[selectedIndex].length} stocks selected
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full">
                      📄 {csvFileName} ({csvStocks.length} stocks)
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Run Scan Button */}
            <div className="mt-6">
              <button
                onClick={handleRunScan}
                disabled={loading}
                className={`w-full md:w-auto px-8 py-3 rounded-md font-semibold text-white transition-colors ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700'
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Running Analysis...
                  </span>
                ) : (
                  '🚀 Run Swing Scan'
                )}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-red-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-red-800 font-medium">{error}</span>
              </div>
            </div>
          )}

          {/* Real-time Progress Section */}
          {loading && (
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <div className="flex items-center mb-4">
                <svg className="animate-spin h-5 w-5 text-orange-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <h3 className="font-semibold text-lg text-gray-800">Analysis in Progress...</h3>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-4 mb-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-orange-600 to-amber-600 h-4 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress * 100}%` }}
                ></div>
              </div>
              
              {/* Progress Message */}
              <div className="flex items-center justify-between text-sm">
                <p className="text-gray-700">{progressMessage}</p>
                <p className="text-gray-600 font-medium">{Math.round(progress * 100)}%</p>
              </div>
            </div>
          )}

          {/* Results Section */}
          {results && results.summary && (
            <div className="space-y-6">
              {/* Summary Card */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">
                    🎯 Analysis Summary
                  </h2>
                  
                  {/* Download Button */}
                  {results.all_results && results.all_results.length > 0 && (
                    <button
                      onClick={handleDownloadResults}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download Results
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <p className="text-sm text-gray-600">Total Analyzed</p>
                    <p className="text-2xl font-bold text-gray-900">{results.summary.total_analyzed}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <p className="text-sm text-green-600">CALL Setups</p>
                    <p className="text-2xl font-bold text-green-700">{results.summary.call_setups}</p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg text-center">
                    <p className="text-sm text-red-600">PUT Setups</p>
                    <p className="text-2xl font-bold text-red-700">{results.summary.put_setups}</p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg text-center">
                    <p className="text-sm text-yellow-600">Near CALL</p>
                    <p className="text-2xl font-bold text-yellow-700">{results.summary.near_call}</p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg text-center">
                    <p className="text-sm text-orange-600">Near PUT</p>
                    <p className="text-2xl font-bold text-orange-700">{results.summary.near_put}</p>
                  </div>
                </div>
              </div>

              {/* Best Breakout Stock */}
              {results.best_breakout_stock && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg shadow-lg p-6">
                  <div className="flex items-center mb-4">
                    <svg className="w-8 h-8 text-green-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <h3 className="text-2xl font-bold text-green-800">⭐ Best Breakout Stock</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-600">Symbol</p>
                      <p className="text-xl font-bold text-gray-900">{results.best_breakout_stock.symbol}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Score</p>
                      <p className="text-xl font-bold text-green-600">{results.best_breakout_stock.score?.toFixed(1)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Price</p>
                      <p className="text-xl font-bold text-gray-900">₹{results.best_breakout_stock.current_price?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Hourly RSI</p>
                      <p className="text-xl font-bold text-gray-900">{results.best_breakout_stock.hourly_rsi?.toFixed(1)}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => handleTradeClick(results.best_breakout_stock.symbol)}
                    className="w-full md:w-auto px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    🎯 Trade This Stock
                  </button>
                </div>
              )}

              {/* CALL Setups */}
              {results.call_stocks && results.call_stocks.length > 0 && (
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                    <h3 className="text-xl font-semibold text-gray-800">
                      🟢 CALL Setups (Bullish Entries)
                    </h3>
                    <span className="ml-3 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                      {results.call_stocks.length} stocks
                    </span>
                  </div>
                  <SwingResultsTable 
                    stocks={getStockDetails(results.call_stocks)}
                    onTradeClick={handleTradeClick}
                    onChartClick={(symbol) => {
                      setSelectedSymbolForChart(symbol);
                      setIsChartModalOpen(true);
                    }}
                    type="call"
                  />
                </div>
              )}

              {/* PUT Setups */}
              {results.put_stocks && results.put_stocks.length > 0 && (
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
                    <h3 className="text-xl font-semibold text-gray-800">
                      🔴 PUT Setups (Bearish Entries)
                    </h3>
                    <span className="ml-3 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                      {results.put_stocks.length} stocks
                    </span>
                  </div>
                  <SwingResultsTable 
                    stocks={getStockDetails(results.put_stocks)}
                    onTradeClick={handleTradeClick}
                    onChartClick={(symbol) => {
                      setSelectedSymbolForChart(symbol);
                      setIsChartModalOpen(true);
                    }}
                    type="put"
                  />
                </div>
              )}

              {/* Near CALL Setups */}
              {results.near_call_stocks && results.near_call_stocks.length > 0 && (
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                    <h3 className="text-xl font-semibold text-gray-800">
                      🟡 Near CALL (Potential Bullish)
                    </h3>
                    <span className="ml-3 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                      {results.near_call_stocks.length} stocks
                    </span>
                  </div>
                  <SwingResultsTable 
                    stocks={getStockDetails(results.near_call_stocks)}
                    onTradeClick={handleTradeClick}
                    onChartClick={(symbol) => {
                      setSelectedSymbolForChart(symbol);
                      setIsChartModalOpen(true);
                    }}
                    type="near_call"
                  />
                </div>
              )}

              {/* Near PUT Setups */}
              {results.near_put_stocks && results.near_put_stocks.length > 0 && (
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
                    <h3 className="text-xl font-semibold text-gray-800">
                      🟠 Near PUT (Potential Bearish)
                    </h3>
                    <span className="ml-3 px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      {results.near_put_stocks.length} stocks
                    </span>
                  </div>
                  <SwingResultsTable 
                    stocks={getStockDetails(results.near_put_stocks)}
                    onTradeClick={handleTradeClick}
                    onChartClick={(symbol) => {
                      setSelectedSymbolForChart(symbol);
                      setIsChartModalOpen(true);
                    }}
                    type="near_put"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {!isMobileEmbed && <Footer />}

      {/* Modals */}
      <OrderTicketModal
        isOpen={isOrderModalOpen}
        onClose={handleModalClose}
        stockSymbol={selectedStockSymbol}
        onSuccess={handleOrderSuccess}
      />

      <SwingHelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />

      {/* Stock Chart Modal */}
      <StockChartModal
        isOpen={isChartModalOpen}
        onClose={() => setIsChartModalOpen(false)}
        symbol={selectedSymbolForChart}
      />
    </div>
  );
};

export default SwingModelPage;
